import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import {Alert, Box, Button, CircularProgress, GlobalStyles, Stack} from "@mui/material";
import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useParams} from "react-router-dom";
import {digitalSigningDetailsKey} from "../digitalSigningQueries";
import {signedPageOf} from "../details/digitalSigningDetails.util";
import {DigitalSigningService} from "../digitalSigning.service";
import DigitalSigningSignedView from "../public/DigitalSigningSignedView";

/** Resolves once the page's web font is there (when the design has one). */
const fontsSettled = () => (typeof document !== 'undefined' && document.fonts?.ready ? document.fonts.ready.then(() => undefined) : Promise.resolve());

/** Resolves once every image in [root] has loaded or failed: the logo and the signature must be on paper. */
const imagesSettled = (root: HTMLElement | null) => Promise.all(
    Array.from(root?.querySelectorAll('img') ?? []).map(image => (
        image.complete ? Promise.resolve() : new Promise<void>(resolve => {
            image.addEventListener('load', () => resolve(), {once: true});
            image.addEventListener('error', () => resolve(), {once: true});
        })
    ))
);

// A4 with quiet margins; on paper the page is white and nothing but the signed page prints
const PRINT_STYLES = {
    '@page': {size: 'A4', margin: '12mm'},
    '@media print': {
        'html, body': {backgroundColor: '#FFFFFF !important'},
        '.digital-signing-print-toolbar': {display: 'none !important'},
    },
};

/** Printable view of what the guest signed. */
const DigitalSigningPrintPage = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const rootRef = useRef<HTMLDivElement>(null);
    const printedRef = useRef(false);

    const query = useQuery(
        digitalSigningDetailsKey(id),
        () => DigitalSigningService.details(id!),
        {enabled: !!id, refetchOnWindowFocus: false, retry: 1}
    );
    const details = query.data;

    useEffect(() => {
        if (!details?.snapshot || printedRef.current) return;

        let cancelled = false;
        void Promise.all([imagesSettled(rootRef.current), fontsSettled()]).then(() => {
            if (cancelled || printedRef.current) return;
            printedRef.current = true;
            window.print();
        });
        return () => {
            cancelled = true;
        };
    }, [details]);

    return (
        <Box sx={{minHeight: '100vh', bgcolor: '#FFFFFF'}}>
            <GlobalStyles styles={PRINT_STYLES}/>

            <Stack
                className={'digital-signing-print-toolbar'}
                direction={'row'}
                justifyContent={'flex-end'}
                gap={1}
                sx={{position: 'sticky', top: 0, zIndex: 1, p: 1.5, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider'}}
            >
                <Button variant={'contained'} startIcon={<PrintIcon/>} disabled={!details?.snapshot} onClick={() => window.print()}>
                    {t('Print')}
                </Button>
                <Button variant={'outlined'} startIcon={<CloseIcon/>} onClick={() => window.close()}>
                    {t('Close')}
                </Button>
            </Stack>

            <Box ref={rootRef} sx={{maxWidth: 720, mx: 'auto'}}>
                {query.isLoading && (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 10}}>
                        <CircularProgress aria-label={t('Loading')}/>
                    </Box>
                )}
                {(query.isError || (details && !details.snapshot)) && (
                    <Alert severity={'error'} sx={{m: 3}}>{t('The signature could not be loaded.')}</Alert>
                )}
                {!!details?.snapshot && (
                    <DigitalSigningSignedView
                        snapshot={details.snapshot}
                        {...signedPageOf(details)}
                        sx={{
                            printColorAdjust: 'exact',
                            WebkitPrintColorAdjust: 'exact',
                            '@media print': {bgcolor: '#FFFFFF'},
                            '& section, & [role="group"], & dl': {breakInside: 'avoid'},
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default DigitalSigningPrintPage;
